package com.example.mystybag.common.ui.components.custom

import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp

/**
 * Cross-platform icon solution that works on both Android and iOS.
 * Uses Unicode text symbols as icons since material.icons are not available on iOS.
 */

object CrossPlatformIcons {
    // Unicode symbols that work on both platforms
    const val Check = "✓"
    const val KeyboardArrowDown = "▼"
    const val KeyboardArrowUp = "▲"
    const val KeyboardArrowLeft = "◄"
    const val KeyboardArrowRight = "►"
    const val Close = "✕"
    const val Add = "+"
    const val Remove = "−"
    /** Pencil / edit icon (Unicode U+270F) */
    const val Edit = "✏"
}

/**
 * Composable function that displays a cross-platform icon using Unicode text symbols.
 * This works on both Android and iOS without requiring material.icons.
 *
 * @param icon The Unicode symbol to display (from CrossPlatformIcons)
 * @param contentDescription Description for accessibility
 * @param modifier Modifier for the icon
 * @param tint Color for the icon (defaults to current content color)
 * @param fontSize Size of the icon text
 */
@Composable
fun CrossPlatformIcon(
    icon: String,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    tint: Color = androidx.compose.ui.graphics.Color.Unspecified,
    fontSize: TextUnit = 20.sp
) {
    val iconColor = if (tint != androidx.compose.ui.graphics.Color.Unspecified) {
        tint
    } else {
        LocalContentColor.current
    }
    
    Text(
        text = icon,
        color = iconColor,
        modifier = modifier,
        fontSize = fontSize,
        fontWeight = FontWeight.Bold
    )
}

/**
 * Convenience function for Check icon
 */
@Composable
fun CheckIcon(
    contentDescription: String? = "Selected",
    modifier: Modifier = Modifier,
    tint: Color = androidx.compose.ui.graphics.Color.Unspecified,
    fontSize: TextUnit = 20.sp
) {
    CrossPlatformIcon(
        icon = CrossPlatformIcons.Check,
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
        fontSize = fontSize
    )
}

/**
 * Convenience function for KeyboardArrowDown icon
 */
@Composable
fun KeyboardArrowDownIcon(
    contentDescription: String? = "Expand",
    modifier: Modifier = Modifier,
    tint: Color = androidx.compose.ui.graphics.Color.Unspecified,
    fontSize: TextUnit = 20.sp
) {
    CrossPlatformIcon(
        icon = CrossPlatformIcons.KeyboardArrowDown,
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
        fontSize = fontSize
    )
}

/**
 * Convenience function for Add icon
 */
@Composable
fun AddIcon(
    contentDescription: String? = "Add",
    modifier: Modifier = Modifier,
    tint: Color = androidx.compose.ui.graphics.Color.Unspecified,
    fontSize: TextUnit = 20.sp
) {
    CrossPlatformIcon(
        icon = CrossPlatformIcons.Add,
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
        fontSize = fontSize
    )
}

/**
 * Convenience function for Edit / Pencil icon
 */
@Composable
fun EditIcon(
    contentDescription: String? = "Edit",
    modifier: Modifier = Modifier,
    tint: Color = androidx.compose.ui.graphics.Color.Unspecified,
    fontSize: TextUnit = 20.sp
) {
    CrossPlatformIcon(
        icon = CrossPlatformIcons.Edit,
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
        fontSize = fontSize
    )
}
